/**
 * Utility functions for the hopingwegetfunded calculator
 * This file contains all the calculation logic extracted from StartupCalculator
 */

/**
 * Calculates effective equity for multiple work periods
 * @param {number} baseEquity - Base equity amount
 * @param {Array} periods - Array of work periods
 * @param {string} employmentType - 'employee' or 'contractor'
 * @returns {number} The effective equity value
 */
export const calculateEffectiveEquityForPeriods = (baseEquity, periods, employmentType) => {
    // No equity for contractors
    if (employmentType === 'contractor') {
        return 0;
    }

    // Ensure periods is an array
    const validPeriods = Array.isArray(periods) ? periods : [];
    if (validPeriods.length === 0) {
        // Default to full equity if no periods defined
        return baseEquity;
    }

    // For equity calculation, we need to:
    // 1. Weight by time spent in each period (months)
    // 2. Weight by hours worked in each period

    let weightedEquity = 0;

    validPeriods.forEach(period => {
        // Calculate months in this period
        const monthsInPeriod = period.endMonth - period.startMonth;
        if (monthsInPeriod <= 0) return;

        // Calculate weighted equity for this period
        const periodEquity = baseEquity * (period.hoursPerWeek / 40) * (monthsInPeriod / 12);

        weightedEquity += periodEquity;
    });

    return weightedEquity;
};

/**
 * Calculates effective salary for a simple work configuration
 * @param {number} baseSalary - Base annual salary
 * @param {number} hoursPerWeek - Hours worked per week
 * @param {string} employmentType - 'employee' or 'contractor'
 * @param {number} hireStartMonth - Month when hire starts (0-12)
 * @returns {number} The effective salary
 */
export const calculateEffectiveSalary = (baseSalary, hoursPerWeek, employmentType, hireStartMonth) => {
    // Adjust for part-time
    let adjustedSalary = baseSalary * (hoursPerWeek / 40);

    // Adjust for contractor (50% premium)
    if (employmentType === 'contractor') {
        adjustedSalary *= 1.5;
    }

    // Adjust for partial year
    if (hireStartMonth > 0) {
        const monthsWorked = 12 - hireStartMonth;
        adjustedSalary = adjustedSalary * (monthsWorked / 12);
    }

    return adjustedSalary;
};

/**
 * Calculates effective salary for multiple work periods
 * @param {number} baseSalary - Base annual salary
 * @param {Array} periods - Array of work periods
 * @param {string} employmentType - 'employee' or 'contractor'
 * @returns {number} The effective salary across all periods
 */
export const calculateEffectiveSalaryForPeriods = (baseSalary, periods, employmentType) => {
    if (!periods || periods.length === 0) return 0;

    let totalSalary = 0;

    periods.forEach(period => {
        // Calculate number of months in this period
        const monthsInPeriod = period.endMonth - period.startMonth;
        if (monthsInPeriod <= 0) return;

        // Calculate fraction of the year
        const yearFraction = monthsInPeriod / 12;

        // Calculate salary for this period based on hours
        let periodSalary = baseSalary * (period.hoursPerWeek / 40) * yearFraction;

        totalSalary += periodSalary;
    });

    // Apply contractor premium at the root level if applicable
    if (employmentType === 'contractor') {
        totalSalary *= 1.5;
    }

    return totalSalary;
};

/**
 * Calculates effective equity for a simple work configuration
 * @param {number} baseEquity - Base equity percentage
 * @param {number} hoursPerWeek - Hours worked per week
 * @param {string} employmentType - 'employee' or 'contractor'
 * @returns {number} The effective equity
 */
export const calculateEffectiveEquity = (baseEquity, hoursPerWeek, employmentType) => {
    // No equity for contractors
    if (employmentType === 'contractor') {
        return 0;
    }

    // Adjust for part-time (proportional to hours)
    return baseEquity * (hoursPerWeek / 40);
};

/**
 * Calculates culture score based on configuration
 * @param {Object} config - Current configuration 
 * @returns {number} Culture score (0-100)
 */
export const calculateCultureScore = (config) => {
    const {
        tristenSalaryTier,
        martinSalaryTier,
        hireDesigner,
        eaType,
        hireSocial,
        workEnvironment,
        operatingResources
    } = config;
    
    let cultureScore = 0;

    // Base score
    cultureScore += 20;

    // Team size impact (each member adds to culture)
    let teamSize = 2; // Tristen and Martin
    if (hireDesigner) teamSize += 1;
    if (eaType !== 'none') teamSize += 1;
    if (hireSocial) teamSize += 1;

    // Team size contribution (diminishing returns)
    cultureScore += Math.min(teamSize * 5, 20);

    // Founder salaries impact
    const avgFounderTier = (tristenSalaryTier + martinSalaryTier) / 2;
    cultureScore += avgFounderTier * 5; // 0-15 points based on tier

    // Work environment impact
    if (workEnvironment === 'garage') cultureScore -= 5;
    if (workEnvironment === 'coworking') cultureScore += 5;
    if (workEnvironment === 'office') cultureScore += 10;

    // Operating resources impact
    switch (operatingResources) {
        case 'survival':
            cultureScore -= 10;
            break;
        case 'sustainable':
            cultureScore += 0;
            break;
        case 'enabled':
            cultureScore += 5;
            break;
        case 'empowered':
            cultureScore += 10;
            break;
    }

    // EA bonuses
    if (eaType === 'regular') {
        cultureScore += 5; // Regular EA culture bonus
    } else if (eaType === 'clinical') {
        cultureScore += 15; // Org Psych EA bigger culture bonus
    }

    // Ensure score is between 0 and 100
    return Math.max(0, Math.min(100, cultureScore));
};

/**
 * Calculates capitalization score based on costs and equity
 * @param {Object} config - Current configuration
 * @returns {number} Capitalization score (0-100)
 */
export const calculateCapitalizationScore = (config) => {
    const {
        totalCosts,
        hireDesigner,
        designerEmploymentType,
        designerEquity,
        eaType,
        eaEmploymentType,
        eaEquity,
        hireSocial,
        socialEmploymentType,
        socialEquity,
        workEnvironment,
        operatingResources,
        location
    } = config;
    
    // Calculate total equity given up
    const totalEquityGivenUp = (
        (hireDesigner && designerEmploymentType !== 'contractor' ? designerEquity : 0) +
        (eaType !== 'none' && eaEmploymentType !== 'contractor' ? eaEquity : 0) +
        (hireSocial && socialEmploymentType !== 'contractor' ? socialEquity : 0)
    );

    // Define capitalization levels based on total costs and equity
    const costRatio = totalCosts / 1000000; // Ratio of costs to 1M
    const equityRatio = totalEquityGivenUp / 10; // Ratio of equity to 10%

    // Calculate score (lower costs and less equity given = better capitalization)
    let capScore = 100;
    capScore -= costRatio * 40; // Up to 40 points deducted for costs
    capScore -= equityRatio * 30; // Up to 30 points deducted for equity

    // Additional factors
    if (location === 'sanfrancisco') {
        capScore -= 10; // SF penalty for higher burn rate
    }

    // Bonus for frugal choices
    if (workEnvironment === 'garage') capScore += 5;
    if (operatingResources === 'survival' || operatingResources === 'sustainable') capScore += 5;

    return Math.max(0, Math.min(100, capScore));
};

/**
 * Define capitalization level based on score
 * @param {number} score - Capitalization score (0-100)
 * @returns {Object} Level information with name, color, and description
 */
export const getCapitalizationLevel = (score) => {
    if (score >= 85) return {
        level: "Exceptional",
        color: "text-green-700",
        description: "Very lean operation with minimal burn rate"
    };
    if (score >= 70) return {
        level: "Strong",
        color: "text-green-600",
        description: "Efficient use of capital with good runway"
    };
    if (score >= 55) return {
        level: "Moderate",
        color: "text-blue-600",
        description: "Balanced approach to spending and equity"
    };
    if (score >= 40) return {
        level: "Concerning",
        color: "text-yellow-600",
        description: "High burn rate may require early funding"
    };
    return {
        level: "Critical",
        color: "text-red-600",
        description: "Unsustainable burn rate, immediate funding needed"
    };
};

/**
 * Define culture level based on score
 * @param {number} score - Culture score (0-100)
 * @returns {Object} Level information with name, color, and description
 */
export const getCultureLevel = (score) => {
    if (score >= 85) return {
        level: "Outstanding",
        color: "text-green-700",
        description: "Exceptional work environment and team morale"
    };
    if (score >= 70) return {
        level: "Strong",
        color: "text-green-600",
        description: "Positive culture supporting productivity"
    };
    if (score >= 55) return {
        level: "Developing",
        color: "text-blue-600",
        description: "Decent foundation with room to grow"
    };
    if (score >= 40) return {
        level: "Challenging",
        color: "text-yellow-600",
        description: "Culture needs attention and investment"
    };
    return {
        level: "Problematic", 
        color: "text-red-600", 
        description: "Major culture issues may impact success"
    };
};

/**
 * Format currency with proper formatting
 * @param {number} amount - Amount to format
 * @param {boolean} showLocationIndicator - Whether to show location indicator
 * @param {string} location - Current location ('stcharles' or 'sanfrancisco')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, showLocationIndicator = false, location = '') => {
    const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
    }).format(amount);

    // Show location indicator if requested and we're in San Francisco
    if (showLocationIndicator && location === 'sanfrancisco') {
        return `${formatted} (SF)`;
    }

    return formatted;
};

/**
 * Apply location multiplier to an amount
 * @param {number} amount - Base amount
 * @param {string} location - Current location ('stcharles' or 'sanfrancisco')
 * @param {number} multiplier - Location multiplier value
 * @returns {number} Amount with multiplier applied if applicable
 */
export const applyLocationMultiplier = (amount, location, multiplier) => {
    // Only apply multiplier if San Francisco is selected
    return location === 'sanfrancisco' ? amount * multiplier : amount;
};